import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext.jsx';
import { businessConfig } from '../config/business.js';
import { getClarityMode, OPEN_CONSENT_EVENT } from '../utils/clarity';

const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer style={{ background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-light)' }}>
      <div className="container section-padding-small">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12">
          {/* Company Info */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="rcm-logo-container" data-testid="footer-logo-container">
                <img
                  src="/rcm-logo-2026.webp"
                  alt="RCM Biosciences"
                  width="122" height="90" loading="lazy" decoding="async"
                  style={{ height: '90px', width: 'auto' }}
                />
              </div>
            </div>
            <p className="body-regular mb-4" style={{ color: 'var(--text-secondary)' }}>
              {t('footer.description')}
            </p>
            <div className="flex flex-col space-y-2">
              <a href={`mailto:${businessConfig.email}`} className="flex items-center gap-2 body-small" style={{ color: 'var(--brand-primary)', textDecoration: 'none' }}>
                <Mail size={16} />
                {businessConfig.email}
              </a>
              <a href={`tel:${businessConfig.phoneSecondaryDigits}`} className="flex items-center gap-2 body-small" style={{ color: 'var(--brand-primary)', textDecoration: 'none' }}>
                <Phone size={16} />
                {businessConfig.phoneSecondary}
              </a>
              <div className="flex items-center gap-2 body-small flex-wrap" style={{ color: 'var(--brand-primary)' }}>
                <Phone size={16} />
                <a href={`tel:${businessConfig.phoneDigits}`} style={{ color: 'var(--brand-primary)', textDecoration: 'none' }}>{businessConfig.phone}</a>
                <a
                  href={`sms:${businessConfig.phoneDigits}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                    padding: '2px 7px', borderRadius: '10px',
                    background: 'rgba(0,229,255,0.12)', color: 'var(--brand-primary)',
                    border: '1px solid rgba(0,229,255,0.3)', textDecoration: 'none', fontSize: '10px', fontWeight: 600,
                  }}
                  data-testid="footer-sms-link"
                >
                  Call or Text
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="heading-3 mb-4">{t('footer.quickLinks')}</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/products" className="body-regular hover:underline" style={{ color: 'var(--text-secondary)' }}>
                  Products
                </Link>
              </li>
              <li>
                <Link to="/how-to-order" className="body-regular hover:underline" style={{ color: 'var(--text-secondary)' }}>
                  How to Order
                </Link>
              </li>
              <li>
                <Link to="/about" className="body-regular hover:underline" style={{ color: 'var(--text-secondary)' }}>
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/quality" className="body-regular hover:underline" style={{ color: 'var(--text-secondary)' }}>
                  Quality Standards
                </Link>
              </li>
              <li>
                <Link to="/contact" className="body-regular hover:underline" style={{ color: 'var(--text-secondary)' }}>
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/blog" className="body-regular hover:underline" style={{ color: 'var(--text-secondary)' }}>
                  Research Blog
                </Link>
              </li>
              <li>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/public/price-list-pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="body-regular hover:underline"
                  style={{ color: 'var(--text-secondary)' }}
                  data-testid="footer-price-list-link"
                >
                  Price List (PDF)
                </a>
              </li>
              <li style={{ display: 'none' }} data-testid="footer-referral-program-link-hidden">
                <Link to="/referral-program" className="body-regular hover:underline" style={{ color: 'var(--text-secondary)' }}>
                  Referral Program
                </Link>
              </li>
              <li>
                <Link to="/invoice-lookup" className="body-regular hover:underline" style={{ color: 'var(--text-secondary)' }}>
                  Invoice Lookup
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="heading-3 mb-4">{t('footer.legal')}</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/disclaimer" className="body-regular hover:underline" style={{ color: 'var(--text-secondary)' }}>
                  {t('footer.disclaimer')}
                </Link>
              </li>
              <li>
                <Link to="/terms" className="body-regular hover:underline" style={{ color: 'var(--text-secondary)' }}>
                  {t('footer.terms')}
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" className="body-regular hover:underline" style={{ color: 'var(--text-secondary)' }}>
                  {t('footer.privacy')}
                </Link>
              </li>
              {getClarityMode() !== 'off' && (
                <li>
                  <button type="button" data-testid="footer-cookie-settings-btn"
                    onClick={() => window.dispatchEvent(new Event(OPEN_CONSENT_EVENT))}
                    className="body-regular hover:underline"
                    style={{ color: 'var(--text-secondary)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                    Cookie Settings
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8" style={{ borderTop: '1px solid var(--border-light)' }}>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="body-small" style={{ color: 'var(--text-light)' }}>
              © {new Date().getFullYear()} {businessConfig.displayName}. {t('footer.allRights')}
            </p>
            <p className="body-small text-center" style={{ color: 'var(--text-light)' }}>
              {t('footer.researchOnly')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
